// ═══ Hercules Cart Drawer ═══════════════════════════════════════
const $ = id => document.getElementById(id);
const qtyTimers = {};
const variantCache = {};
let cartAutoCloseTimer;
let refreshInFlight = false;
let refreshPending = false;
let toastTimer;
let gwpInFlight = false;
let gwpPending = false;
let cartCache = null;
let refreshPromise = null;

const _origFetch = window.fetch.bind(window);

function getCartEl() { return document.getElementById('cart'); }

// Variants currently configured as active gifts.
function getConfiguredGiftVariants() {
  const el = getCartEl();
  if (!el) return [];
  return [el.dataset.gwp1Variant, el.dataset.gwp2Variant, el.dataset.subGwpVariant]
    .filter(Boolean).map(String);
}

// Every variant that has EVER been a gift (current + retired). Retired IDs come from
// data-all-gwp-variants so gifts you've swapped out can still be swept from old carts.
function getAllGiftVariants() {
  const el = getCartEl();
  const retired = (el?.dataset.allGwpVariants || '')
    .split(',').map(s => s.trim()).filter(Boolean);
  return Array.from(new Set([...getConfiguredGiftVariants(), ...retired]));
}

// LP tub-tier gifts: added by the lp-tub-tiers section, controlled by tub
// quantity (not the spend-threshold GWP slots). They carry `_lp_gift` so the
// threshold sweep below leaves them alone, but they must still be treated as
// free (excluded from paid totals) and clamped to qty 1.
function isLpGiftItem(item) {
  return !!(item && item.properties && item.properties._lp_gift);
}

// A line is a gift if it carries our hidden flag OR matches any known gift variant.
/* Re-check an _lp_gift line against the cart it now finds itself in.

   The buy box stamps the unlock rule onto the line when it adds the gift:
     _lp_gift_min    tubs required    (quantity gifts)
     _lp_gift_spend  cart cents required (spend gifts)
     _lp_gift_mode   'subscription'   (subscribe-only) | 'both' | 'onetime'
     _lp_gift_pid    the paid product the gift was earned against

   Gifts added BEFORE those properties existed - and any added by the
   lp-tub-tiers section, which does not stamp them - have no rule to check, so
   they qualify by default. That keeps this backwards compatible: the worst
   case is the old behaviour, never a gift removed for the wrong reason.

   Only NON-gift lines count toward the quantity, or two gifts would prop each
   other up. */
function lpGiftStillQualifies(item, cart) {
  const props = (item && item.properties) || {};

  const mode = props._lp_gift_mode || '';
  if (mode === 'subscription') {
    const stillSubscribed = cart.items.some(
      i => !isGiftItem(i) && i.selling_plan_allocation != null
    );
    if (!stillSubscribed) return false;
  }
  if (mode === 'onetime') {
    const allSubscribed = cart.items
      .filter(i => !isGiftItem(i))
      .every(i => i.selling_plan_allocation != null);
    if (allSubscribed) return false;
  }

  /* Spend gifts re-check against the WHOLE cart excluding gifts, not just the
     product they came from. The buy box could only measure the supply being
     chosen; by the time the cart runs this the shopper may have added other
     products, and a spend threshold should count all of it. */
  const spend = parseInt(props._lp_gift_spend, 10);
  if (spend > 0 && getNonGwpTotal(cart) < spend) return false;

  const min = parseInt(props._lp_gift_min, 10);
  if (min > 1) {
    const pid = String(props._lp_gift_pid || '');
    const qty = cart.items.reduce((total, i) => {
      if (isGiftItem(i)) return total;
      // No pid recorded: fall back to the whole cart rather than removing a
      // gift we cannot attribute.
      if (pid && String(i.product_id) !== pid) return total;
      return total + i.quantity;
    }, 0);
    if (qty < min) return false;
  }

  return true;
}

function isGiftItem(item) {
  if (item && item.properties && (item.properties._gwp || item.properties._gift)) return true;
  if (isLpGiftItem(item)) return true;
  return getAllGiftVariants().includes(String(item.variant_id));
}

// Kept for backwards-compatibility with existing callers.
function getGwpVariants() { return getAllGiftVariants(); }

function getNonGwpTotal(cart) {
  return cart.items.reduce((t, i) => isGiftItem(i) ? t : t + i.final_line_price, 0);
}

function formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }
function formatMoneyClean(cents) {
  const dollars = (Number(cents) || 0) / 100;
  return '$' + (Number.isInteger(dollars) ? dollars.toFixed(0) : dollars.toFixed(2));
}
function escapeHTML(value) {
  return String(value ?? '').replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));
}
function cleanRewardLabel(label) {
  return escapeHTML(String(label || '').replace(/\s+at\s+\$?\d+(?:\.\d+)?\s*$/i, '').trim());
}
function rewardIconHTML(key) {
  const icons = {
    shipping: '<svg viewBox="0 0 24 24" focusable="false"><path d="M3 7h11v9H3z"></path><path d="M14 10h4l3 3v3h-7z"></path><circle cx="7" cy="18" r="1.8"></circle><circle cx="18" cy="18" r="1.8"></circle></svg>',
    gwp1: '<svg viewBox="0 0 24 24" focusable="false"><path d="M4 10h16v10H4z"></path><path d="M3 6h18v4H3z"></path><path d="M12 6v14"></path><path d="M12 6c-1.2-2.3-5.1-2-5.1.4 0 1.4 1.5 2.3 5.1 2.3"></path><path d="M12 6c1.2-2.3 5.1-2 5.1.4 0 1.4-1.5 2.3-5.1 2.3"></path></svg>',
    gwp2: '<svg viewBox="0 0 24 24" focusable="false"><path d="M6 8h12l1 12H5z"></path><path d="M9 8a3 3 0 0 1 6 0"></path><path d="M9 13h6"></path></svg>',
    subscription: '<svg viewBox="0 0 24 24" focusable="false"><path d="M17 2l4 4-4 4"></path><path d="M3 11V9a4 4 0 0 1 4-4h14"></path><path d="M7 22l-4-4 4-4"></path><path d="M21 13v2a4 4 0 0 1-4 4H3"></path></svg>',
    check: '<svg viewBox="0 0 24 24" focusable="false"><path d="M20 6 9 17l-5-5"></path></svg>'
  };
  return icons[key] || icons.gwp1;
}

function rewardTierHTML(tier) {
  const state = tier.added ? 'added' : tier.unlocked ? 'unlocked' : 'locked';
  const meta = tier.added ? `${tier.fullLabel || tier.label} added to cart` : tier.unlocked ? `${tier.fullLabel || tier.label} unlocked` : `${tier.fullLabel || tier.label} unlocks at ${formatMoneyClean(tier.threshold)}`;
  return `
    <div class="reward-tier reward-icon-tier ${state}" data-tier="${tier.key}" title="${meta}">
      <span class="reward-dot" aria-hidden="true">${rewardIconHTML(tier.key)}</span>
      <span class="reward-name">${tier.displayLabel}</span>
    </div>`;
}

function openCart(options = {}) {
  const shouldRefresh = options.refresh !== false;
  const shouldRenderCache = options.renderCache !== false;

  // Render the last known cart before the drawer slides in, so progress bars
  // are already visually correct when the customer sees the drawer.
  if (shouldRenderCache && cartCache) applyCart(cartCache);

  const cartEl = $('cart');
  const overlayEl = $('overlay');
  if (cartEl) cartEl.classList.add('open');
  if (overlayEl) overlayEl.classList.add('open');
  document.body.style.overflow = 'hidden';

  if (shouldRefresh) refreshCart();
}

function closeCart() {
  $('cart').classList.remove('open');
  $('overlay').classList.remove('open');
  document.body.style.overflow = '';
}

function resetAutoClose() {}

function refreshCart() {
  if (refreshInFlight) { refreshPending = true; return refreshPromise || Promise.resolve(cartCache); }
  refreshInFlight = true;
  refreshPromise = _origFetch('/cart.js')
    .then(r => { if (r.status === 429) throw new Error('Rate limited'); return r.json(); })
    .then(cart => {
      cartCache = cart;
      applyCart(cart);
      return cart;
    })
    .catch(err => {
      console.warn('[cart] refresh failed:', err.message);
      return cartCache;
    })
    .finally(() => {
      refreshInFlight = false;
      refreshPromise = null;
      if (refreshPending) { refreshPending = false; setTimeout(refreshCart, 300); }
    });
  return refreshPromise;
}

function refreshCartFresh() {
  if (!refreshInFlight) return refreshCart();
  return (refreshPromise || Promise.resolve()).then(() => refreshCart());
}

function applyCart(cart) {
  if (!cart) return;
  cartCache = cart;
  updateBadge(cart.item_count);
  reRenderItems(cart);
  updateSubGwpLabel(cart);
  handleGWP(cart);
}

function updateBadge(count) {
  document.querySelectorAll('.cart-badge, #hbadge, #cpill').forEach(el => el.textContent = count);
  document.querySelectorAll('cart-count').forEach(el => {
    el.textContent = count;
    el.setAttribute('data-count', count);
  });
}

function updateShippingBar(totalCents, cart) {
  const bar = $('ship-bar');
  if (!bar) return;
  const state = getProgressState(cart || cartCache);
  const title = $('reward-main-lbl');
  const status = $('reward-status');
  const fill = $('reward-fill');
  const tiers = $('reward-tiers');
  const sub = $('sub-gwp-lbl');

  if (title) title.innerHTML = state.mainLabel;
  if (status) status.textContent = state.statusText;
  if (fill) {
    fill.style.setProperty('--reward-fill', `${state.rewardValue}%`);
    fill.style.setProperty('--reward-count', state.tierCount);
  }
  if (tiers) tiers.innerHTML = state.tiersHtml;
  if (sub) {
    sub.hidden = state.subHidden;
    sub.className = `sub-reward ${state.subUnlocked ? 'unlocked' : 'locked'}`;
    sub.innerHTML = state.subLabelHtml;
  }
}

function updateFooter(cart) {
  const foot = $('foot');
  const scrollFoot = $('scroll-foot');
  if (foot) foot.style.display = cart.item_count === 0 ? 'none' : '';
  if (scrollFoot) scrollFoot.style.display = cart.item_count === 0 ? 'none' : '';
  if (!scrollFoot) return;

  const gwpVariants = getGwpVariants();
  const nonGwpItems = cart.items.filter(i => !gwpVariants.includes(String(i.variant_id)) && i.final_price > 0);
  const lineTotal = nonGwpItems.reduce((t, i) => t + i.final_line_price, 0);
  /* Savings = compare-at minus paid, NOT just Shopify's applied discounts.

     original_line_price vs final_line_price only captures discounts Shopify
     APPLIED - automatic discounts, codes, subscription plans. A bundle is
     different: it is priced low outright, so Shopify sees a $206 item sold at
     $206 and reports zero discount. The real saving is compare_at vs price,
     which is why the cart said $50 while the buy box said hundreds.

     /cart.js line items do NOT carry compare_at_price. It is on the Liquid
     variant object and on /products/<handle>.js, so compare-at is fetched per
     PRODUCT and cached for the life of the page - a handful of requests once,
     not per render.

     Savings render immediately from the applied discounts, then increase when
     the lookup lands. Never blocks the drawer opening. */
  const savingsFromDiscounts = (items) =>
    items.reduce((t, i) => t + Math.max(0, i.original_line_price - i.final_line_price), 0);

  const savingsWithCompareAt = (items) =>
    items.reduce((t, i) => {
      const applied = Math.max(0, i.original_line_price - i.final_line_price);
      const compareAt = COMPARE_AT_CACHE[String(i.variant_id)];
      const versusRrp = compareAt
        ? Math.max(0, compareAt * i.quantity - i.final_line_price)
        : 0;
      /* Larger of the two, never the sum: a bundle with a compare-at AND a
         quantity discount must not be counted twice. */
      return t + Math.max(applied, versusRrp);
    }, 0);

  const savings = savingsWithCompareAt(nonGwpItems);

  // Order-level discounts (e.g. the quiz code) live on the cart, not the line items.
  // cart.total_price is AFTER all discounts; the gap vs the line total is the code's reduction.
  const orderDiscount = Math.max(0, lineTotal - cart.total_price);
  const finalTotal = (typeof cart.total_price === 'number') ? cart.total_price : lineTotal;

  const sub = $('sub'), total = ($('total') || $('foot-now'));
  // Subtotal shows the line total (after line/qty discounts); total shows the real payable amount.
  if (sub) sub.textContent = formatMoney(lineTotal);
  if (total) total.textContent = formatMoney(finalTotal);

  /* _origFetch, not window.fetch: the patched fetch triggers a cart refresh on
     every call, and refreshing from inside a render would loop. */
  loadCompareAtPrices(nonGwpItems, () => {
    const updated = savingsWithCompareAt(nonGwpItems);
    const row = $('sav-row'), amt = $('sav-amt');
    if (row && amt) {
      if (updated > 0) {
        amt.textContent = '−' + formatMoney(updated);
        row.style.display = '';
      } else {
        row.style.display = 'none';
      }
    }

    updateFootTotal(cart);

    /* Repaint each line's struck price in place rather than re-rendering the
       drawer. A full re-render would run applyCart -> handleGWP, and writing to
       the cart from inside a render is how you get a refresh loop. */
    nonGwpItems.forEach((i) => {
      const compareAt = COMPARE_AT_CACHE[String(i.variant_id)];
      if (!compareAt) return;
      const was = Math.max(i.original_line_price, compareAt * i.quantity);
      if (was <= i.final_line_price) return;
      document
        .querySelectorAll('[data-was-for="' + i.variant_id + '"]')
        .forEach((el) => { el.textContent = formatMoney(was); });
    });
  });

  const savRow = $('sav-row'), savAmt = $('sav-amt');
  if (savRow && savAmt) {
    if (savings > 0) {
      savAmt.textContent = '−' + formatMoney(savings);
      savRow.style.display = '';
    } else {
      savRow.style.display = 'none';
    }
  }

  // Discount-code line: show the order-level reduction (quiz code) so the drawer total matches checkout.
  var discRow = $('cart-code-row'), discAmt = $('cart-code-amt');
  if (discRow && discAmt) {
    if (orderDiscount > 0) {
      discAmt.textContent = '−' + formatMoney(orderDiscount);
      discRow.style.display = '';
    } else {
      discRow.style.display = 'none';
    }
  }

  // Shipping row: "Free" once the free-shipping threshold is met (uses same total + threshold as the progress bar)
  const shipCostEl = $('ship-cost');
  if (shipCostEl) {
    const el = getCartEl();
    const shipThreshold = (el ? parseFloat(el.dataset.freeShipThreshold) || 130 : 130) * 100;
    if (finalTotal >= shipThreshold) {
      shipCostEl.textContent = 'Free';
      shipCostEl.classList.add('ship-free');
    } else {
      shipCostEl.textContent = 'Calculated at checkout';
      shipCostEl.classList.remove('ship-free');
    }
  }
}

function updateSubGwpLabel(cart) {
  const lbl = $('sub-gwp-lbl');
  if (!lbl) return;
  const state = getProgressState(cart || cartCache);
  lbl.hidden = state.subHidden;
  lbl.className = `sub-reward ${state.subUnlocked ? 'unlocked' : 'locked'}`;
  lbl.innerHTML = state.subLabelHtml;
}

/* variant_id -> compare_at_price, in cents. Populated from
   /products/<handle>.js because /cart.js does not expose compare_at_price on
   line items. One request per distinct product per page; results are kept for
   the life of the page, and a product already being fetched is not fetched
   again. */
const COMPARE_AT_CACHE = {};
const COMPARE_AT_PENDING = {};

function loadCompareAtPrices(items, onDone) {
  const handles = {};
  items.forEach((item) => {
    if (COMPARE_AT_CACHE[String(item.variant_id)] !== undefined) return;
    const handle = (item.url || '').split('?')[0].split('/products/')[1];
    if (handle && !COMPARE_AT_PENDING[handle]) handles[handle] = true;
  });

  const list = Object.keys(handles);
  if (!list.length) return;

  let outstanding = list.length;
  list.forEach((handle) => {
    COMPARE_AT_PENDING[handle] = true;
    _origFetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((product) => {
        (product.variants || []).forEach((variant) => {
          /* null is a real answer - "this variant has no compare-at" - and is
             cached so we never re-request it. */
          COMPARE_AT_CACHE[String(variant.id)] =
            typeof variant.compare_at_price === 'number' ? variant.compare_at_price : null;
        });
      })
      .catch(() => {})
      .then(() => {
        outstanding -= 1;
        if (outstanding === 0 && typeof onDone === 'function') onDone();
      });
  });
}

function handleGWP(cart) {
  const el = getCartEl();
  if (!el || !cart) return;

  // If a GWP mutation is already running, mark a re-run so we never drop a reconciliation.
  if (gwpInFlight) { gwpPending = true; return; }

  const paidTotal = getNonGwpTotal(cart);
  const paidCount = cart.items.filter(i => !isGiftItem(i)).length;
  const noPaidProduct = paidCount === 0 || paidTotal <= 0;   // HARD RULE source

  const subVariant = String(el.dataset.subGwpVariant || '');
  const hasSub = cart.items.some(
    i => i.selling_plan_allocation != null && String(i.variant_id) !== subVariant
  );

  // Active gift slots and their qualifying condition.
  const tiers = [
    { key: 'gwp1', variantId: String(el.dataset.gwp1Variant || ''),
      enabled: el.dataset.gwp1Enabled === 'true',
      qualify: () => paidTotal >= (parseFloat(el.dataset.gwp1Threshold) * 100 || Infinity) },
    { key: 'gwp2', variantId: String(el.dataset.gwp2Variant || ''),
      enabled: el.dataset.gwp2Enabled === 'true',
      qualify: () => paidTotal >= (parseFloat(el.dataset.gwp2Threshold) * 100 || Infinity) },
  ];
  if (el.dataset.subGwpEnabled === 'true' && subVariant) {
    tiers.push({ key: 'sub', variantId: subVariant, enabled: true, qualify: () => hasSub });
  }

  const removals = [];   // { id, quantity }
  const seen = {};       // ensures only ONE of each gift survives

  // 1. Walk every gift line already in the cart and decide if it stays.
  cart.items.forEach(item => {
    if (!isGiftItem(item)) return;

    // LP tub-tier gifts are governed by the lp-tub-tiers section, not the
    // spend-threshold slots. Don't sweep them for failing a threshold — but do
    // remove them if the cart has no paid product left, and clamp to qty 1 so
    // shoppers can't stack multiples of a free gift.
    if (isLpGiftItem(item)) {
      if (noPaidProduct) {
        removals.push({ id: item.key, quantity: 0 });
      } else if (!lpGiftStillQualifies(item, cart)) {
        // Its unlock condition no longer holds - tubs reduced below the
        // threshold, or the subscription removed.
        removals.push({ id: item.key, quantity: 0 });
      } else if (item.quantity > 1) {
        removals.push({ id: item.key, quantity: 1 });
      }
      return;
    }

    const vid = String(item.variant_id);
    const tier = tiers.find(t => t.enabled && t.variantId && t.variantId === vid);
    const justified = !noPaidProduct && !!tier && tier.qualify() && !seen[vid] && item.quantity >= 1;

    if (justified) {
      seen[vid] = true;
      if (item.quantity > 1) removals.push({ id: item.key, quantity: 1 }); // clamp to 1
    } else {
      removals.push({ id: item.key, quantity: 0 }); // orphan / disabled / unqualified / $0 cart
    }
  });

  // 2. Add newly-qualified gifts that aren't present yet (skip entirely if no paid product).
  const present = cart.items.filter(isGiftItem).map(i => String(i.variant_id));
  const additions = [];
  if (!noPaidProduct) {
    tiers.forEach(t => {
      if (t.enabled && t.variantId && t.qualify() && !present.includes(t.variantId)) {
        additions.push({ id: t.variantId, key: t.key });
      }
    });
  }

  if (!removals.length && !additions.length) return;

  gwpInFlight = true;

  // Fade out anything we're about to remove for a smooth UI.
  removals.forEach(r => { if (r.quantity === 0) {
    const node = cart.items.find(i => i.key === r.id);
    if (node) fadeOutItem(node.key);
  }});

  // Run mutations sequentially - Shopify's cart API is happier serialised than parallel.
  const post = (url, body) => _origFetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  }).then(r => r.json());

  let chain = Promise.resolve();
  removals.forEach(r => { chain = chain.then(() => post('/cart/change.js', { id: r.id, quantity: r.quantity })); });
  additions.forEach(a => { chain = chain.then(() => post('/cart/add.js', { id: a.id, quantity: 1, properties: { _gwp: a.key } })); });

  chain
    .then(() => _origFetch('/cart.js').then(r => r.json()))
    .then(updated => {
      cartCache = updated;
      updateBadge(updated.item_count);
      updateShippingBar(getNonGwpTotal(updated), updated);
      updateFooter(updated);
      reRenderItems(updated);
      updateSubGwpLabel(updated);
    })
    .catch(err => console.warn('[cart] GWP sync failed:', err.message))
    .finally(() => {
      gwpInFlight = false;
      if (gwpPending) { gwpPending = false; setTimeout(() => handleGWP(cartCache), 150); }
    });
}


function clampProgress(value) {
  value = Number(value) || 0;
  return Math.max(0, Math.min(100, value));
}

function getMilestoneProgress(totalCents, tiers) {
  const activeTiers = tiers.filter(t => t.enabled);
  if (!activeTiers.length) return 100;

  const positions = activeTiers.map((_, index) => ((index + 0.5) / activeTiers.length) * 100);
  const firstTier = activeTiers[0];
  const lastTier = activeTiers[activeTiers.length - 1];

  if (totalCents >= lastTier.threshold) return 100;
  if (totalCents <= 0) return 0;

  if (totalCents < firstTier.threshold) {
    return clampProgress((totalCents / Math.max(firstTier.threshold, 1)) * positions[0]);
  }

  for (let i = 1; i < activeTiers.length; i++) {
    const prev = activeTiers[i - 1];
    const current = activeTiers[i];
    if (totalCents < current.threshold) {
      const segmentTotal = Math.max(current.threshold - prev.threshold, 1);
      const segmentNow = Math.max(totalCents - prev.threshold, 0);
      const segmentProgress = segmentNow / segmentTotal;
      return clampProgress(positions[i - 1] + segmentProgress * (positions[i] - positions[i - 1]));
    }
  }

  return 100;
}

function getProgressState(cart) {
  const el = getCartEl();
  const totalCents = cart ? getNonGwpTotal(cart) : parseInt(el?.dataset.initialTotal || '0', 10) || 0;
  const items = cart?.items || [];
  const shipThreshold = (parseFloat(el?.dataset.freeShipThreshold) || 130) * 100;
  const gwp1Threshold = parseFloat(el?.dataset.gwp1Threshold) * 100 || 15000;
  const gwp2Threshold = parseFloat(el?.dataset.gwp2Threshold) * 100 || 20000;
  const gwp1Variant = el?.dataset.gwp1Variant || '';
  const gwp2Variant = el?.dataset.gwp2Variant || '';
  const subVariant = el?.dataset.subGwpVariant || '';
  const gwp1Enabled = el?.dataset.gwp1Enabled === 'true';
  const gwp2Enabled = el?.dataset.gwp2Enabled === 'true';
  const subEnabled = el?.dataset.subGwpEnabled === 'true';
  const gwp1Label = cleanRewardLabel(el?.dataset.gwp1Label || 'Free Mystery Box');
  const gwp2Label = cleanRewardLabel(el?.dataset.gwp2Label || 'Free Duffel Bag');
  const subLabel = cleanRewardLabel(el?.dataset.subGwpLabel || 'Free gift with subscription');
  const gwp1InCart = items.some(i => String(i.variant_id) === String(gwp1Variant));
  const gwp2InCart = items.some(i => String(i.variant_id) === String(gwp2Variant));
  const hasSub = items.some(i => i.selling_plan_allocation != null && String(i.variant_id) !== String(subVariant));

  const tiers = [
    { key: 'shipping', label: 'Free shipping', fullLabel: 'Free shipping', displayLabel: 'Shipping', threshold: shipThreshold, unlocked: totalCents >= shipThreshold, added: false, enabled: true },
    { key: 'gwp1', label: gwp1Label, fullLabel: gwp1Label, displayLabel: 'Gift 1', threshold: gwp1Threshold, unlocked: totalCents >= gwp1Threshold, added: gwp1InCart, enabled: gwp1Enabled },
    { key: 'gwp2', label: gwp2Label, fullLabel: gwp2Label, displayLabel: 'Gift 2', threshold: gwp2Threshold, unlocked: totalCents >= gwp2Threshold, added: gwp2InCart, enabled: gwp2Enabled }
  ].filter(t => t.enabled).sort((a, b) => a.threshold - b.threshold);

  const highestThreshold = Math.max(...tiers.map(t => t.threshold), 1);
  const nextTier = tiers.find(t => !t.unlocked);
  const unlockedCount = tiers.filter(t => t.unlocked).length;
  const mainLabel = nextTier
    ? `Add <strong>${formatMoneyClean(nextTier.threshold - totalCents)}</strong> more to get ${nextTier.label}`
    : '<strong>All rewards unlocked.</strong>';

  return {
    rewardValue: getMilestoneProgress(totalCents, tiers),
    mainLabel,
    statusText: `${unlockedCount}/${tiers.length} unlocked`,
    tierCount: tiers.length,
    tiersHtml: tiers.map(rewardTierHTML).join(''),
    subHidden: !subEnabled || !subVariant,
    subUnlocked: hasSub,
    subLabelHtml: (!subEnabled || !subVariant) ? '' : `
      <span class="sub-reward-dot" aria-hidden="true">${hasSub ? rewardIconHTML('check') : rewardIconHTML('subscription')}</span>
      <span class="sub-reward-copy">
        <strong>${hasSub ? 'Free Welcome Kit unlocked' : 'First time subscribe for Free Welcome Kit'}</strong>
        <span></span>
      </span>`
  };
}

function shipBarHTML(cart = cartCache) {
  const state = getProgressState(cart);
  return `
    <div class="ship-bar rewards-bar rewards-bar--inline" id="ship-bar">
      <div class="reward-inline-main">
        <div class="reward-copy-line">
          <span class="reward-title" id="reward-main-lbl">${state.mainLabel}</span>
        </div>
        <div class="reward-inline-track" id="reward-fill" style="--reward-fill:${state.rewardValue}%; --reward-count:${state.tierCount};" aria-label="Cart reward progress">
          <span class="reward-track-line" aria-hidden="true"></span>
          <span class="reward-track-fill" aria-hidden="true"></span>
          <div class="reward-tiers reward-icon-row" id="reward-tiers">${state.tiersHtml}</div>
        </div>
      </div>
      <div class="sub-reward ${state.subUnlocked ? 'unlocked' : 'locked'}" id="sub-gwp-lbl"${state.subHidden ? ' hidden' : ''}>${state.subLabelHtml}</div>
    </div>`;
}

function itemHTML(item, gwpVariants) {
  const isFree = item.final_price === 0 || isGiftItem(item);
  /* Struck price must agree with the Savings row. That row counts compare-at,
     so this has to as well - otherwise a bundle showed "was $647.67, now
     $589.38" ($58) beside a savings line of $103.57, because the strike was
     using original_line_price (pre-DISCOUNT) while savings used compare-at.

     Larger of the two, matching savingsWithCompareAt(): never the sum. */
  const compareAtLine = COMPARE_AT_CACHE[String(item.variant_id)];
  const wasLinePrice = Math.max(
    item.original_line_price,
    compareAtLine ? compareAtLine * item.quantity : 0
  );
  const hasDiscount = wasLinePrice > item.final_line_price;
  const isSub = item.selling_plan_allocation?.selling_plan != null;
  const variantLine = item.variant_title && item.variant_title !== 'Default Title'
    ? `<div class="ivar">${escapeHTML(item.variant_title)}</div>` : '';

  /* Line item properties - what is actually INSIDE a bundle.

     Simple Bundles records each component choice as properties[Component
     Label]. Those reach the order and the packing slip, but this drawer only
     ever rendered variant_title, so a bundle looked like a single anonymous
     line and nobody could see or check the components before ordering.

     Underscore-prefixed keys are skipped: Shopify treats them as hidden, and
     they are internal plumbing here - _bundle_selection (the joined string
     that DRIVES the components, already shown individually below), and the
     _lp_gift_* rules the gift reconciler stamps on. Showing those would be
     noise at best and confusing at worst.

     Values come from an app and from merchant-entered labels, so both key and
     value are escaped. */
  /* Shorten the component label to the product name.

     Simple Bundles writes the whole product title plus the option type, e.g.
       "100% Creatine Monohydrate - Memory & Performance Supplement - Size (Dropdown 1)"
     which is six lines of cart at body size. Everything after the first
     " - " or " – " is marketing copy the shopper does not need here - they
     already know what they bought. Splitting on the first dash leaves
     "100% Creatine Monohydrate", which is what they would call it.

     Both dash characters are handled: the titles mix hyphen and en dash. */
  const shortLabel = (key) =>
    String(key)
      .split(/\s+[-–—]\s+/)[0]
      .replace(/\s*\((?:dropdown|select|choose)[^)]*\)\s*$/i, '')
      .trim();

  const propLines = Object.keys(item.properties || {})
    .filter((key) => key.charAt(0) !== '_')
    .filter((key) => {
      const v = item.properties[key];
      return v !== null && v !== undefined && String(v).trim() !== '';
    })
    .map((key) =>
      `<div class="iprop"><span class="iprop-k">${escapeHTML(shortLabel(key))}</span>${escapeHTML(String(item.properties[key]))}</div>`
    )
    .join('');
  const propsBlock = propLines ? `<div class="iprops">${propLines}</div>` : '';
  const subLine = isSub
    ? `<span class="sub-tag">Subscribe & Save</span><div class="ifreq">${item.selling_plan_allocation.selling_plan.name}</div>` : '';
  const qtyBlock = isFree ? '<div></div>' : `
    <div class="qty">
      <button type="button" onclick="chQty('${item.key}',-1)">−</button>
      <input id="q-${item.key}" class="qty-input" type="number" min="0" value="${item.quantity}" data-prev="${item.quantity}"
        oninput="setQty('${item.key}',this.value)" onfocus="this.select()"/>
      <button type="button" onclick="chQty('${item.key}',1)">+</button>
    </div>`;
  const priceBlock = `
    <div class="iprice">
      ${(isFree && item.final_price > 0) || (!isFree && hasDiscount) ? `<div class="was" data-was-for="${item.variant_id}">${formatMoney(wasLinePrice)}</div>` : ''}
      <div class="now ${isFree ? 'now-free' : ''}" id="p-${item.key}">${isFree ? 'Included' : formatMoney(item.final_line_price)}</div>
    </div>`;
  const removeBtn = isFree ? '' : `
    <button class="rm" onclick="rmItem('${item.key}')" title="Remove" aria-label="Remove item">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
        <path d="M10 11v6M14 11v6"></path>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
      </svg>
    </button>`;
  const thumbInner = item.image
    ? `<img src="${item.image}" alt="${item.product_title}">`
    : item.product_title.slice(0,4).toUpperCase();

  const productUrl = (item.url || '#').split('?')[0];
  const nameBlock = isFree
    ? `<div class="iname">${item.product_title}</div>`
    : `<a class="iname" href="${productUrl}" onclick="closeCart()">${item.product_title}</a>`;

  return `
    <div class="item" data-key="${item.key}">
      <div class="thumb">${thumbInner}</div>
      <div class="info">
        ${nameBlock}
        ${variantLine}
        ${propsBlock}
        ${subLine}
        <div class="ifoot">
          ${qtyBlock}
          ${priceBlock}
        </div>
      </div>
      ${removeBtn}
    </div>`;
}

/* Compact gift list. Deliberately minimal: no images, no prices, no quantity
   controls and no remove button - the reconciler owns those, and a remove
   button on something the cart re-adds immediately just looks broken.

   Everything comes from the cart payload already in memory, so it costs
   nothing to render. */
function giftListHTML(gifts) {
  if (!gifts.length) return '';

  const rows = gifts.map(g => {
    /* Small square, showing the actual item. loading="lazy" because these sit
       below the fold in a long cart and are the last thing that should compete
       for bandwidth with the product images above. */
    const thumb = g.image
      ? `<img src="${g.image}" alt="" loading="lazy">`
      : '';
    return `
      <div class="gift-row">
        <span class="gift-sq">${thumb}</span>
        <span class="gift-nm">${escapeHTML(g.product_title)}</span>
        <span class="gift-free">Free</span>
      </div>`;
  }).join('');

  return `
    <div class="gift-rows">
      <div class="gift-rows-head">Your free gifts</div>
      ${rows}
    </div>`;
}

function scrollFootHTML(cart) {
  const el = getCartEl();
  const shipThreshold = (el ? parseFloat(el.dataset.freeShipThreshold) || 130 : 130) * 100;
  const nonGwpTotal = getNonGwpTotal(cart);
  const shipRow = nonGwpTotal >= shipThreshold
    ? `<span id="ship-cost" class="ship-free">Free</span>`
    : `<span id="ship-cost">Calculated at checkout</span>`;
  return `
    <div class="cart-scroll-foot" id="scroll-foot"${cart.item_count === 0 ? ' style="display:none"' : ''}>
      <div class="sum-section-label">Summary</div>
      <div class="sum-row"><span>Subtotal</span><span id="sub">${formatMoney(cart.total_price)}</span></div>
      <div class="sum-row sav" id="sav-row" style="display:none"><span>Savings</span><span id="sav-amt"></span></div>
      <div class="sum-row sav" id="cart-code-row" style="display:none"><span>Discount code applied</span><span id="cart-code-amt"></span></div>
      <div class="sum-chips" id="sum-chips" style="display:none"></div>
      <div class="sum-row"><span>Shipping</span>${shipRow}</div>
      ${giftListHTML(cart.items.filter(isGiftItem))}
      <!-- Total moved to the pinned footer (.foot-total). Two totals in one
           drawer is worse than none: the shopper has to work out which is
           authoritative, and they disagree for a frame on every re-render. -->
      </div>
      <div id="upsell-block"></div>
    </div>`;
}

function reRenderItems(cart) {
  const container = $('cart-items');
  const foot = $('foot');
  if (!container) return;
  const gwpVariants = getGwpVariants();

  if (cart.item_count === 0) {
    _origFetch('/cart.js').then(r => r.json()).then(fresh => {
      if (fresh.item_count > 0) return reRenderItems(fresh);
      container.innerHTML = shipBarHTML(fresh) + `
        <div class="empty" id="empty-state"><h3>Oops! Your cart is empty</h3></div>
        <div id="rec-block"></div>` + scrollFootHTML(fresh);
      if (foot) foot.style.display = 'none';
      updateShippingBar(0, fresh);
      renderRecs(fresh);
    }).catch(err => console.warn('[cart] verify failed:', err.message));
    return;
  }

  if (foot) foot.style.display = '';
  /* Gifts render AFTER the summary, not among the paid lines.

     Four gifts as full cart lines outnumbered the thing they actually bought
     and pushed the total below the fold. Under the summary they are
     confirmation - "yes, this is coming" - which is all they need to be once
     the shopper has already decided.

     No thumbnails and no price lookups on purpose: names only, straight from
     the cart payload, so this adds zero requests and no image loads. */
  const paidLines = cart.items.filter(i => !isGiftItem(i));
  const giftLines = cart.items.filter(i => isGiftItem(i));

  container.innerHTML = shipBarHTML(cart) +
    paidLines.map(i => itemHTML(i, gwpVariants)).join('') +
    `<div id="rec-block"></div>` + scrollFootHTML(cart);

  updateFootTotal(cart);
  updateSumChips(cart);
  updateShippingBar(getNonGwpTotal(cart), cart);
  updateFooter(cart);
  renderUpsell(cart);
}

function updatePriceUI(key, newQty, prevQty) {
  const pEl = $('p-' + key);
  if (!pEl || pEl.textContent === 'Included' || pEl.textContent === '$0.00' || prevQty <= 0) return;

  const currentLine = parseFloat(pEl.textContent.replace('$', ''));
  const unit = currentLine / prevQty;
  const newLine = unit * newQty;
  const delta = newLine - currentLine;
  pEl.textContent = '$' + newLine.toFixed(2);

  const subEl = $('sub');
  if (subEl) {
    const cur = parseFloat(subEl.textContent.replace('$', ''));
    subEl.textContent = '$' + Math.max(0, cur + delta).toFixed(2);
  }

  const totalEl = ($('total') || $('foot-now'));
  if (totalEl) {
    const cur = parseFloat(totalEl.textContent.replace('$', ''));
    totalEl.textContent = '$' + Math.max(0, cur + delta).toFixed(2);
  }

  const qtyDiff = newQty - prevQty;
  document.querySelectorAll('.cart-badge, #hbadge, #cpill').forEach(el => {
    const cur = parseInt(el.textContent) || 0;
    el.textContent = Math.max(0, cur + qtyDiff);
  });
}

function chQty(key, delta) {
  const qEl = $('q-' + key);
  if (!qEl) return;
  const currentQty = parseInt(qEl.value) || 0;
  const newQty = Math.max(0, currentQty + delta);
  if (newQty === 0) { rmItem(key); return; }
  qEl.value = newQty;
  updatePriceUI(key, newQty, currentQty);
  debounceCartUpdate(key);
}

function setQty(key, value) {
  const newQty = parseInt(value);
  if (isNaN(newQty) || newQty < 1) return;
  const qEl = $('q-' + key);
  const prevQty = parseInt(qEl?.dataset.prev) || newQty;
  updatePriceUI(key, newQty, prevQty);
  if (qEl) qEl.dataset.prev = newQty;
  debounceCartUpdate(key);
}

function debounceCartUpdate(key) {
  clearTimeout(qtyTimers[key]);
  qtyTimers[key] = setTimeout(() => {
    const finalQty = parseInt($('q-' + key)?.value) || 0;
    if (finalQty === 0) { rmItem(key); return; }
    _origFetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: finalQty })
    }).then(r => r.json()).then(applyCart).catch(err => console.warn('[cart] qty update failed:', err.message));
  }, 400);
}

function fadeOutItem(key) {
  const el = document.querySelector(`[data-key="${key}"]`);
  if (el) { el.classList.add('removing'); setTimeout(() => el.remove(), 160); }
}

function rmItem(key) {
  fadeOutItem(key);
  _origFetch('/cart/change.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  }).then(r => r.json()).then(applyCart).catch(err => console.warn('[cart] remove failed:', err.message));
  hcToast('Removed');
}

function getSlots(prefix, count) {
  const el = getCartEl();
  if (!el) return [];
  const slots = [];
  for (let i = 1; i <= count; i++) {
    const variantId = el.dataset[`${prefix}${i}Variant`];
    if (variantId) slots.push({
      variantId, index: i,
      discountCode: el.dataset[`${prefix}${i}Discount`] || '',
      discountPct: parseFloat(el.dataset[`${prefix}${i}DiscountPct`]) || 0,
      /* Per-slot copy. Empty string means "not set" and falls back to the
         shared text, so a merchant only writes the fields that differ. */
      head: el.dataset[`${prefix}${i}Head`] || '',
      body: el.dataset[`${prefix}${i}Body`] || '',
      note: el.dataset[`${prefix}${i}Note`] || ''
    });
  }
  return slots;
}

function fetchVariant(variantId) {
  if (variantCache[variantId]) return Promise.resolve(variantCache[variantId]);
  try {
    const cached = sessionStorage.getItem(`hc_variant_${variantId}`);
    if (cached) {
      const v = JSON.parse(cached);
      variantCache[variantId] = v;
      return Promise.resolve(v);
    }
  } catch(e) {}

  return _origFetch(`/variants/${variantId}.js`).then(r => r.json()).then(v => {
    const needsProductFallback = !v.featured_image && (v.product_id || v.featured_image?.product_id);
    if (needsProductFallback) {
      const pid = v.product_id || v.featured_image.product_id;
      return _origFetch(`/products/${pid}.js`).then(r => r.json()).then(p => {
        if (!v.featured_image && p.images?.[0]) v.featured_image = { src: p.images[0].src || p.images[0] };
        if (!v.product_title) v.product_title = p.title;
        return cacheVariant(variantId, v);
      });
    }
    return cacheVariant(variantId, v);
  });
}

function cacheVariant(variantId, v) {
  variantCache[variantId] = v;
  try { sessionStorage.setItem(`hc_variant_${variantId}`, JSON.stringify(v)); } catch(e) {}
  return v;
}

function renderProductCard(v, onclick, discountPct = 0) {
  const pct = parseFloat(discountPct) || 0;
  const discounted = pct > 0 ? Math.round(v.price * (1 - pct / 100)) : null;
  const imgUrl = v.featured_image?.src || v.featured_image?.url || null;
  const imgInner = imgUrl
    ? `<img src="${imgUrl}" alt="${v.product_title || v.name}" loading="lazy" decoding="async">`
    : `<div class="pc-img-fallback">${(v.product_title || v.name).slice(0,4).toUpperCase()}</div>`;
  const priceHTML = discounted
    ? `<div class="pc-save">${pct}% off</div><div class="pc-pricing"><span class="pc-was">${formatMoney(v.price)}</span><span class="pc-price">${formatMoney(discounted)}</span></div>`
    : `<div class="pc-price">${formatMoney(v.price)}</div>`;
  return `
    <div class="pc">
      <div class="pc-img">${imgInner}</div>
      <div class="pc-name">${v.product_title || v.name}</div>
      ${priceHTML}
      <button class="pc-btn" type="button" onclick="${onclick}">Add to Cart</button>
    </div>`;
}

function renderRecs(cart) {
  const block = $('rec-block');
  if (!block || cart.item_count > 0) { if (block) block.innerHTML = ''; return; }
  const el = getCartEl();
  if (!el) { block.innerHTML = ''; return; }
  const cols = [1, 2, 3, 4].map(i => ({
    label: el.dataset[`col${i}Label`],
    url: el.dataset[`col${i}Url`],
    img: el.dataset[`col${i}Img`]
  })).filter(c => c.label && c.url);
  if (!cols.length) { block.innerHTML = ''; return; }
  block.innerHTML = `
    <div class="col-grid">
      ${cols.map(c => `
        <a class="col-card" href="${c.url}" onclick="closeCart()">
          <div class="col-tile-wrap">${c.img ? `<img class="col-img" src="${c.img}" alt="${c.label}" loading="lazy">` : ''}</div>
          <div class="col-label">${c.label}</div>
        </a>`).join('')}
    </div>`;
}

/* Footer total + compare-at, and the discount chips.

   The saving here is the SAME calculation the line items use - larger of the
   applied discount and the compare-at gap - so the footer can never contradict
   the Savings row above it. */
function updateFootTotal(cart) {
  const now = document.getElementById('foot-now');
  const was = document.getElementById('foot-was');
  const off = document.getElementById('foot-off');
  if (!now) return;

  const paid = cart.items.filter(i => !isGiftItem(i));

  const total = cart.total_price;
  const wasTotal = paid.reduce((t, i) => {
    const compareAt = COMPARE_AT_CACHE[String(i.variant_id)];
    const versusRrp = compareAt ? compareAt * i.quantity : 0;
    return t + Math.max(i.original_line_price, versusRrp);
  }, 0);

  now.textContent = formatMoney(total);

  const saving = wasTotal - total;
  const show = saving > 0 && wasTotal > 0;
  if (was) {
    was.hidden = !show;
    if (show) was.textContent = formatMoney(wasTotal);
  }
  if (off) {
    off.hidden = !show;
    if (show) off.textContent = Math.round((saving / wasTotal) * 100) + '% Off';
  }
}

function updateSumChips(cart) {
  const box = document.getElementById('sum-chips');
  if (!box) return;

  const el = getCartEl();
  const paid = cart.items.filter(i => !isGiftItem(i));

  /* Each chip is shown only when the thing it claims is actually true, so it
     never advertises a discount the shopper is not getting. */
  const chips = [];
  if (paid.some(i => i.selling_plan_allocation)) {
    chips.push(el?.dataset.chipSubscription || 'SAVING W/ SUBSCRIPTION');
  }
  if (paid.some(i => i.original_line_price > i.final_line_price)) {
    chips.push(el?.dataset.chipBundle || 'BUNDLE SAVING');
  }
  if (cart.items.some(isGiftItem)) {
    chips.push(el?.dataset.chipGift || 'FREE + GIFT');
  }

  if (!chips.length) { box.style.display = 'none'; box.innerHTML = ''; return; }

  box.innerHTML = chips.map(c =>
    `<span class="sum-chip"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 3 12V4a1 1 0 0 1 1-1h8a2 2 0 0 1 1.4.6l7.2 7.2a2 2 0 0 1 0 2.6z"/><circle cx="7.5" cy="7.5" r="1.3" fill="currentColor" stroke="none"/></svg>${escapeHTML(c)}</span>`
  ).join('');
  box.style.display = '';
}

/* ============================================================
   UPSELL STRIP

   One product at a time inside a full-width band, with arrows to page
   through the rest.

   A row of four small cards asks the shopper to compare and choose, which
   is work at the point they have already decided to buy. One card with a
   headline and a reason is a single yes/no, and the arrow lets the curious
   browse without putting a shelf in front of everyone else.

   Variants are fetched once and cached in _upsellCache, so paging is
   instant and re-renders do not re-request. State lives on the module, not
   the DOM, so a cart refresh does not reset which card was showing.
   ============================================================ */

let _upsellIndex = 0;
const _upsellCache = {};

function renderUpsell(cart) {
  const block = $('upsell-block');
  if (!block || cart.item_count === 0) { if (block) block.innerHTML = ''; return; }

  const el = getCartEl();
  const cartVariantIds = cart.items.map(i => String(i.variant_id));
  const slots = getSlots('upsell', 4).filter(s => !cartVariantIds.includes(String(s.variantId)));

  if (!slots.length) { block.innerHTML = ''; return; }

  /* Anything already added shortens the list, so clamp rather than letting
     the index point past the end. */
  if (_upsellIndex >= slots.length) _upsellIndex = 0;

  /* Shared copy is the fallback; each slot may override any of the three.
     Rendered inside paint() rather than once, because it now changes as the
     shopper pages - the pitch has to match the product on screen. */
  const sharedHead = el?.dataset.upsellHeading  || 'Complete your stack';
  const sharedBody = el?.dataset.upsellBody     || '';
  const sharedNote = el?.dataset.upsellFootnote || '';

  block.innerHTML = `
    <div class="ups">
      <div class="ups-head" id="ups-head"></div>
      <div class="ups-body" id="ups-body"></div>
      <div class="ups-note" id="ups-note"></div>
      <div class="ups-stage">
        <button class="ups-arrow ups-prev" type="button" aria-label="Previous suggestion"${slots.length < 2 ? ' hidden' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div class="ups-card" id="ups-card"></div>
        <button class="ups-arrow ups-next" type="button" aria-label="Next suggestion"${slots.length < 2 ? ' hidden' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>
      ${slots.length > 1 ? `<div class="ups-dots" id="ups-dots"></div>` : ''}
    </div>`;

  const paint = () => {
    const card = $('ups-card');
    if (!card) return;
    const slot = slots[_upsellIndex];

    const headEl = $('ups-head'), bodyEl = $('ups-body'), noteEl = $('ups-note');
    const head = slot.head || sharedHead;
    const body = slot.body || sharedBody;
    const note = slot.note || sharedNote;
    if (headEl) { headEl.textContent = head; headEl.hidden = !head; }
    if (bodyEl) { bodyEl.textContent = body; bodyEl.hidden = !body; }
    if (noteEl) { noteEl.textContent = note; noteEl.hidden = !note; }

    const dots = $('ups-dots');
    if (dots) {
      dots.innerHTML = slots.map((_, n) =>
        `<span class="ups-dot${n === _upsellIndex ? ' is-on' : ''}"></span>`
      ).join('');
    }

    const cached = _upsellCache[slot.variantId];
    if (!cached) {
      card.innerHTML = `<div class="ups-skeleton"></div>`;
      fetchVariant(slot.variantId)
        .then(v => { _upsellCache[slot.variantId] = v; if (slots[_upsellIndex] === slot) paint(); })
        .catch(err => console.warn('[cart] upsell fetch failed:', err.message));
      return;
    }

    const pct = parseFloat(slot.discountPct) || 0;
    const discounted = pct > 0 ? Math.round(cached.price * (1 - pct / 100)) : null;
    const img = cached.featured_image?.src || cached.featured_image?.url || '';

    card.innerHTML = `
      <div class="ups-thumb">${img ? `<img src="${img}" alt="" loading="lazy" decoding="async">` : ''}</div>
      <div class="ups-info">
        <div class="ups-name">${escapeHTML(cached.product_title || cached.name)}</div>
        <div class="ups-price">
          ${discounted ? `<s>${formatMoney(cached.price)}</s> <strong>${formatMoney(discounted)}</strong>`
                       : `<strong>${formatMoney(cached.price)}</strong>`}
          ${pct > 0 ? `<span class="ups-off">${pct}% off</span>` : ''}
        </div>
      </div>
      <button class="ups-add" type="button" data-ups-add="${cached.id}" data-ups-slot="${slot.index}">Add</button>`;
  };

  const step = (delta) => {
    _upsellIndex = (_upsellIndex + delta + slots.length) % slots.length;
    paint();
  };

  block.querySelector('.ups-prev')?.addEventListener('click', () => step(-1));
  block.querySelector('.ups-next')?.addEventListener('click', () => step(1));

  paint();
}

/* Delegated so it survives the strip being re-rendered on every cart change. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-ups-add]');
  if (!button || button.dataset.busy) return;
  button.dataset.busy = '1';
  const original = button.textContent;
  button.textContent = 'Adding...';
  Promise.resolve(
    addProduct(button.getAttribute('data-ups-add'), 'upsell', Number(button.getAttribute('data-ups-slot')))
  ).catch(() => {}).then(() => {
    delete button.dataset.busy;
    button.textContent = original;
  });
});

function addProduct(variantId, source, slotIndex) {
  const btn = event.target;
  const card = btn.closest('.pc');
  if (card) {
    card.style.opacity = '0.4';
    card.style.pointerEvents = 'none';
    btn.textContent = '✓ Added';
  }
  const el = getCartEl();
  const discountCode = (source === 'upsell' && el && slotIndex)
    ? el.dataset[`upsell${slotIndex}Discount`] || null : null;

  _origFetch('/cart/add.js', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
  .then(r => r.json())
  .then(addedItem => {
    const pct = el && slotIndex ? parseFloat(el.dataset[`upsell${slotIndex}DiscountPct`]) || 0 : 0;
    const effectivePrice = pct > 0 ? Math.round(addedItem.price * (1 - pct / 100)) : addedItem.price;
    const sub = $('sub'), total = ($('total') || $('foot-now'));
    if (sub) sub.textContent = formatMoney(parseFloat(sub.textContent.replace('$','')) * 100 + effectivePrice);
    if (total) total.textContent = formatMoney(parseFloat(total.textContent.replace('$','')) * 100 + effectivePrice);

    if (discountCode) {
      return _origFetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: discountCode })
      }).then(r => r.json());
    }
    return _origFetch('/cart.js').then(r => r.json());
  })
  .then(applyCart)
  .then(() => hcToast('Added to cart'))
  .catch(err => {
    console.error('[cart] add failed:', err);
    if (card) { card.style.opacity = ''; card.style.pointerEvents = ''; btn.textContent = 'Add to Cart'; }
  });
}

function hcToast(msg) {
  let el = document.querySelector('.hc-toast');
  if (!el) { el = document.createElement('div'); el.className = 'hc-toast'; document.body.appendChild(el); }
  el.textContent = msg;
  el.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('show'), 2200);
}

window.fetch = function(...args) {
  const url = args[0];
  const isAdd = typeof url === 'string' && url.includes('/cart/add');
  return _origFetch.apply(this, args).then(response => {
    if (isAdd && response.ok) {
      setTimeout(() => {
        refreshCartFresh().then(() => openCart({ refresh: false, renderCache: false }));
      }, 0);
    }
    return response;
  });
};

const _origXhrOpen = XMLHttpRequest.prototype.open;
const _origXhrSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.open = function(method, url, ...rest) {
  this._isCartAdd = typeof url === 'string' && url.includes('/cart/add');
  return _origXhrOpen.call(this, method, url, ...rest);
};
XMLHttpRequest.prototype.send = function(...args) {
  if (this._isCartAdd) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) {
        setTimeout(() => {
          refreshCartFresh().then(() => openCart({ refresh: false, renderCache: false }));
        }, 0);
      }
    });
  }
  return _origXhrSend.apply(this, args);
};

document.addEventListener('DOMContentLoaded', () => {
  const overlay = document.getElementById('overlay');
  if (overlay && overlay.parentElement !== document.body) document.body.appendChild(overlay);

  // Hijack cart icon clicks
  document.addEventListener('click', e => {
    const cartBtn = e.target.closest('.cart-drawer-button');
    if (!cartBtn) return;
    e.preventDefault();
    e.stopPropagation();
    openCart();
  });

  refreshCart().catch(err => console.warn('[cart] init failed:', err.message));

  // Don't let a cart of only free gifts reach checkout.
  document.addEventListener('click', e => {
    const chk = e.target.closest('#chk-btn, a.chk');
    if (!chk) return;
    const cart = cartCache;
    if (!cart) return;
    if (getNonGwpTotal(cart) <= 0) {
      e.preventDefault();
      hcToast('Add a product to your cart before checking out');
      handleGWP(cart); // sweep any lingering gifts
    }
  }, true);

  // Outside-click closes drawer
  document.addEventListener('click', e => {
    const cart = document.getElementById('cart');
    if (!cart?.classList.contains('open')) return;
    if (cart.contains(e.target)) return;
    if (e.target.closest('.cart-drawer-button')) return;
    closeCart();
  });

  document.addEventListener('cart:updated', refreshCart);
  document.addEventListener('theme:cart:change', refreshCart);

  const cartCountEl = document.querySelector('cart-count');
  if (cartCountEl) {
    new MutationObserver(() => {
      const count = parseInt(cartCountEl.textContent);
      if (!isNaN(count)) document.querySelectorAll('#cpill').forEach(el => el.textContent = count);
    }).observe(cartCountEl, { childList: true, characterData: true, subtree: true });
  }
});